import { useCallback, useEffect, useState } from 'react';

import { Box, ButtonBase } from '@mui/material';

import Catalog from '@/assets/icons/Catalog';
import Typography from '@/components/Typography';
import { useSpotCatalog } from '@/features/Catalog/api';
import { CatalogModal } from '@/features/Dashboard/form/AppForm/components/CatalogModal';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import type { TabSymbol } from '@/features/Dashboard/form/AppForm/context/types';
import { AppType } from '@/features/Dashboard/types';
import {
  clearPendingAppSymbols,
  setSymbols
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';

import DragDropContainer from './DragDropContainer';

export const ItemSelection = () => {
  const { addSymbols, maxItems, form } = useAppForm();
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const pendingAppSymbols = useAppSelector(s => s.symbolSelector.pendingAppSymbols);
  const { data: spotCatalog } = useSpotCatalog();

  // Auto-inject symbols when this form was opened from the selector's "Create app" flow
  useEffect(() => {
    if (!pendingAppSymbols?.length || !spotCatalog) return;
    const type = form.state.values?.type;
    const isForwardCurves = type === AppType.FWD;

    // For FWD charts the selector stores one SymbolKey per period for each selected curve.
    // Group by curve key (Code|PeriodType|TimeRef) and take the first of each group so that
    // addSymbols() receives exactly one entry per curve.
    const sourceSymbols = isForwardCurves
      ? Object.values(
          Object.groupBy(pendingAppSymbols, s => `${s.Code}|${s.PeriodType}|${s.TimeRef}`)
        ).map(group => group![0])
      : pendingAppSymbols;

    const symbolsToAdd: TabSymbol[] = sourceSymbols
      .map(symbol => {
        const spot = spotCatalog.find(item => {
          const baseMatch =
            item.Symbol.Code === symbol.Code &&
            item.Symbol.PeriodType === symbol.PeriodType &&
            item.Symbol.TimeRef === symbol.TimeRef;
          if (!baseMatch) return false;
          return isForwardCurves ? true : item.Symbol.Period === symbol.Period;
        });
        if (!spot) return null;
        return { shortTitle: spot.Title || '', symbol, group: 'Prod_Indexes' as const };
      })
      .filter(Boolean) as TabSymbol[];

    if (symbolsToAdd.length) {
      addSymbols(symbolsToAdd, maxItems);
    }
    dispatch(clearPendingAppSymbols());
  }, [pendingAppSymbols, spotCatalog, form.state.values?.type, addSymbols, maxItems, dispatch]);

  const handleClick = useCallback(() => {
    const symbols = form.state.values?.symbols;
    dispatch(
      setSymbols(
        symbols
          ? [
              ...symbols.active.map(symbol => symbol.symbol),
              ...symbols.backlog.map(symbol => symbol.symbol)
            ]
          : []
      )
    );
    setOpen(true);
  }, [dispatch, form.state.values?.symbols]);
  return (
    <form.Subscribe selector={s => s.values?.type}>
      {type => (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            p: 2
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              mb: 3
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flex: 1
                }}
              >
                <Box>
                  <Typography
                    size={14}
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    Item selection
                  </Typography>
                  <Typography
                    size={12}
                    sx={{ fontWeight: 500, color: 'info.dark', mb: 2 }}
                  >
                    Browse and select {type === AppType.FWD ? 'curves' : 'items'} for your app
                  </Typography>
                </Box>
                <ButtonBase
                  sx={{
                    width: 180,
                    height: 33,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'gx.actionBarButtonTile'
                  }}
                  onClick={handleClick}
                >
                  <Catalog sx={{ fontSize: 14, mr: 1 }} />
                  <Typography sx={{ fontSize: 14 }}>Add from catalog</Typography>
                </ButtonBase>
              </Box>

              <DragDropContainer
                itemsName={AppType.FWD === type ? 'curve' : 'item'}
                maxCapacity={maxItems}
                setOpen={setOpen}
              />
            </Box>
          </Box>
          {open && (
            <CatalogModal
              addItemsToBacklog={symbols => {
                addSymbols(symbols, maxItems);
              }}
              setOpen={setOpen}
            />
          )}
        </Box>
      )}
    </form.Subscribe>
  );
};
